/**
 * ClaimPage — links an approved seller application to the logged-in user
 * account (EPIC-006, Task 6.3).
 *
 * Route: `/sellers/claim` (auth-required via `RequireAuth` in App.tsx).
 *
 * Behavior:
 *   1. Reads `?token=` from the URL. If missing → show an error card with
 *      support hint (cannot redirect to login because we have no token to
 *      preserve).
 *   2. On mount → `POST /sellers/claim { token }`.
 *   3. On 200 → navigate `/sellers/me/products` + success toast.
 *   4. On 400 `seller.claim_invalid` → error card with support hint.
 *
 * Login redirect: handled by `RequireAuth` — the wrapper redirects an
 * unauthenticated visitor to `/login?return_to=/sellers/claim?token=…`
 * which returns them here after login (per `RequireAuth.tsx` implementation).
 *
 * Tokens: `--g-*` only. aria-live. i18n `seller` namespace (ru/ky/en).
 */
import { useEffect, useState, type ReactElement } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  SELLER_NS,
  apiSellerClaim,
  registerSellerI18n,
} from "@/features/seller-registration/api";
import { ApiError } from "@/lib/api-client";

type ClaimState =
  | { kind: "loading" }
  | { kind: "error"; messageKey: string }
  | { kind: "done" };

export function ClaimPage(): ReactElement {
  registerSellerI18n();
  const { t } = useTranslation(SELLER_NS);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [state, setState] = useState<ClaimState>(
    token ? { kind: "loading" } : { kind: "error", messageKey: "claim.noToken" },
  );

  useEffect(() => {
    if (!token) return;

    void (async () => {
      try {
        await apiSellerClaim(token);
        setState({ kind: "done" });
        toast.success(t("claim.successToast"));
        navigate("/sellers/me/products", { replace: true });
      } catch (err) {
        const code = err instanceof ApiError ? err.code : undefined;
        const messageKey =
          code === "seller.claim_invalid" ? "claim.errorDetail" : "claim.errorGeneric";
        setState({ kind: "error", messageKey });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div
      className="flex min-h-dvh flex-col items-center justify-center bg-[var(--g-ground)] px-5"
      data-testid="claim-page"
    >
      <div className="w-full max-w-sm">
        {state.kind === "loading" ? (
          <div
            role="status"
            aria-live="polite"
            aria-label={t("claim.loading")}
            className="flex flex-col items-center gap-4"
            data-testid="claim-loading"
          >
            <Loader2
              size={36}
              className="animate-spin text-primary"
              aria-hidden="true"
            />
            <p className="text-sm text-muted-foreground">{t("claim.loading")}</p>
          </div>
        ) : state.kind === "error" ? (
          <div
            role="alert"
            aria-live="assertive"
            className={[
              "rounded-[var(--g-radius)] border border-destructive/40",
              "bg-destructive/5 p-6 text-center",
            ].join(" ")}
            data-testid="claim-error"
          >
            <h1 className="mb-2 text-lg font-bold text-destructive">
              {t("claim.errorTitle")}
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">
              {t(state.messageKey)}
            </p>
            <a
              href="mailto:support@garden.kg"
              className={[
                "inline-flex min-h-[44px] items-center rounded-[var(--g-radius)]",
                "bg-primary px-4 text-sm font-semibold text-[var(--g-on-primary)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              ].join(" ")}
              data-testid="claim-support-link"
            >
              {t("claim.support")}
            </a>
          </div>
        ) : (
          /* done — navigation in progress */
          <div
            role="status"
            aria-live="polite"
            className="flex flex-col items-center gap-4"
            data-testid="claim-done"
          >
            <Loader2
              size={36}
              className="animate-spin text-primary"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ClaimPage;
